import { Triangle, Compass, Ruler, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";

const sections = [
  {
    icon: Triangle,
    term: "Pyramid",
    def: "A monumental structure with a broad, typically square or rectangular base whose sides rise to converge toward a single point (or a flattened summit). Pyramids appear independently across Egypt, Mesoamerica, Nubia, Asia and beyond — a form that recurs among civilisations with no proven contact.",
  },
  {
    icon: Ruler,
    term: "Megalith",
    def: "A large stone used to construct a monument, either alone or together with other stones. Many pyramids incorporate megalithic blocks weighing tens or hundreds of tonnes, raising unresolved questions about ancient quarrying, transport and lifting techniques.",
  },
  {
    icon: Compass,
    term: "Archaeoastronomy",
    def: "The study of how ancient peoples understood and aligned their monuments to celestial phenomena. Numerous pyramids exhibit precise solstice, equinox or stellar alignments — a recurring signature of intentional astronomical design.",
  },
  {
    icon: HelpCircle,
    term: "Anomaly",
    def: "In this archive, an anomaly is any measurable feature — geometric precision, acoustic behaviour, unexplained voids, or improbable dating — that current mainstream explanations do not fully resolve. We document evidence on all sides.",
  },
];

const glossary = [
  ["Casing Stone", "The smooth outer limestone or granite blocks that originally sheathed a pyramid's stepped core."],
  ["Corbel Vault", "A chamber roof built by progressively overlapping stone courses — used in the Grand Gallery of Giza."],
  ["Muon Tomography", "A non-invasive imaging technique using cosmic-ray particles to 'see' hidden voids inside stone masses."],
  ["Orthostat", "A large upright stone block set as part of a wall or facing."],
  ["Precession", "The slow ~25,772-year wobble of Earth's axis, relevant to dating claimed stellar alignments."],
  ["Shicra", "Woven reed bags filled with stone, used as flexible, seismic-resistant fill at Caral, Peru."],
];

export default function Definitions() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-8 lg:px-12 py-8">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Reference</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">Definitions</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground leading-relaxed">
        A shared vocabulary for reading the archive. We separate what is documented and measurable
        from what remains genuinely open.
      </p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sections.map((s) => (
          <div key={s.term} className="bg-card border border-border p-6">
            <s.icon className="h-6 w-6 text-accent" />
            <h2 className="mt-3 font-serif text-2xl font-bold text-foreground">{s.term}</h2>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.def}</p>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <h2 className="font-serif text-3xl font-semibold text-foreground mb-6">Glossary</h2>
        <dl className="divide-y divide-border border-y border-border">
          {glossary.map(([term, def]) => (
            <div key={term} className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-5">
              <dt className="font-mono text-xs uppercase tracking-widest text-accent">{term}</dt>
              <dd className="sm:col-span-2 text-sm text-muted-foreground leading-relaxed">{def}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-14 bg-secondary/50 border border-border p-8 text-center">
        <h3 className="font-serif text-2xl font-bold text-foreground">Ready to explore?</h3>
        <p className="mt-2 text-sm text-muted-foreground">Begin with the interactive map or browse the full catalogue.</p>
        <div className="mt-5 flex justify-center gap-3">
          <Link to="/map" className="bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-accent/90 transition-colors">Open Map</Link>
          <Link to="/archive" className="border border-foreground/30 font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-foreground/5 transition-colors">Archive Index</Link>
        </div>
      </div>
    </div>
  );
}
